import { useMemo } from 'react';

import { useSession } from '@/context/SessionContext';
import { useTenantFeatureMap } from './useTenantFeatureMap';

const API_GATEWAY_FEATURE_KEY = 'api.gateway.enabled';
const GLOBAL_API_GATEWAY_PERMISSIONS = ['platform.manage'];
const TENANT_API_GATEWAY_PERMISSION = 'tenant.api-gateway.manage';

export interface ApiGatewayAccessState {
  featureMap?: Record<string, boolean>;
  featureTenantId?: string;
  apiGatewayFeatureEnabled: boolean;
  apiGatewayPermissionGranted: boolean;
  accessEnabled: boolean;
}

const normalize = (value: string) => value.trim().toLowerCase();

export function useApiGatewayAccess(): ApiGatewayAccessState {
  const { session } = useSession();
  const { featureMap, featureTenantId } = useTenantFeatureMap();

  return useMemo(() => {
    const apiGatewayFeatureEnabled = !!featureMap?.[API_GATEWAY_FEATURE_KEY];
    const permissionSummary = session?.permissionSummary;
    const scopes = session?.scopes ?? [];

    const hasGlobalPermission = permissionSummary?.global?.some(perm =>
      GLOBAL_API_GATEWAY_PERMISSIONS.some(target => normalize(target) === normalize(perm))
    );

    const hasTenantPermission = featureTenantId
      ? permissionSummary?.tenants?.[featureTenantId]?.some(
          perm => normalize(perm) === TENANT_API_GATEWAY_PERMISSION
        )
      : false;

    const accessFromScopes = scopes.some(scope => {
      const normalized = normalize(scope);
      return (
        normalized === TENANT_API_GATEWAY_PERMISSION ||
        GLOBAL_API_GATEWAY_PERMISSIONS.some(target => normalized === normalize(target))
      );
    });

    const apiGatewayPermissionGranted = Boolean(hasGlobalPermission || hasTenantPermission || accessFromScopes);

    return {
      featureMap,
      featureTenantId,
      apiGatewayFeatureEnabled,
      apiGatewayPermissionGranted,
      accessEnabled: apiGatewayFeatureEnabled && apiGatewayPermissionGranted
    };
  }, [featureMap, featureTenantId, session?.permissionSummary, session?.scopes]);
}
